import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Accordion,
  AccordionItem,
  Autocomplete,
  AutocompleteItem,
  Button,
  Card,
  CardBody,
  Chip,
  Input,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  softBadgeColors,
} from '@structural-climbing/ui'
import {
  competitionBoards,
  gradeDisplay,
  type CompetitionDiscipline,
} from '@structural-climbing/validation'
import { useGymGradeConfig } from '../../layouts/useGymGradeConfig'
import { useGymContext } from '../../contexts/GymContext'
import { MemberIdentity } from '../members/MemberIdentity'
import { CompetitorProfileModal, type CompetitorTarget } from './CompetitorProfileModal'
import { MemberSearchInput } from '../members/MemberSearchInput'
import type { GymSection } from '../routes/types'
import { CompetitionRouteDetailModal } from './CompetitionRouteDetailModal'
import { holdColorName } from './RouteIdentity'
import {
  fullName,
  groupLabel,
  routeDiscipline,
  type CompetitionEntry,
  type CompetitionResult,
  type CompetitionRound,
  type CompetitionRouteRef,
} from './types'

/** The filter key for "no filter"; never a real id, which are all uuids. */
const ALL = 'all'

interface Props {
  competitionId: string
  discipline: CompetitionDiscipline
  /** The competition's sections, for grading each route on its own scale. */
  sections: GymSection[]
  results: CompetitionResult[]
  entries: CompetitionEntry[]
  routes: CompetitionRouteRef[]
  /** For naming a route's round in its detail; absent on a single-day competition. */
  rounds?: CompetitionRound[]
  onRefresh: () => void
}

export function ResultsTab({
  competitionId,
  discipline,
  sections,
  results,
  entries,
  routes,
  rounds = [],
  onRefresh,
}: Props) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const gradeConfig = useGymGradeConfig()
  const { primaryColor } = useGymContext()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [addUserId, setAddUserId] = useState<string | null>(null)
  const [addRouteId, setAddRouteId] = useState<string | null>(null)
  const [addAttempts, setAddAttempts] = useState('')
  const [profileTarget, setProfileTarget] = useState<CompetitorTarget | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState(ALL)
  // null until staff open or close someone themselves; until then a list
  // narrowed to one competitor opens on its own.
  const [expanded, setExpanded] = useState<Set<string> | null>(null)
  const [detailRef, setDetailRef] = useState<CompetitionRouteRef | null>(null)

  // Numbers restart per discipline in a combined competition, so "1" alone
  // would not say which wall. Name the discipline there.
  const combined = discipline === 'combined'
  const nameFor = (ref: CompetitionRouteRef | undefined, fallback: string) => {
    const label = ref?.label ?? fallback
    if (!combined || !ref) return label
    const wall = routeDiscipline(discipline, ref.route?.section_id, sections)
    return `${t(`competitions.discipline_${wall}`)} · ${label}`
  }
  const refFor = (routeId: string) => routes.find((r) => r.route_id === routeId)
  const labelFor = (routeId: string) => nameFor(refFor(routeId), '—')
  const wallOf = (sectionId: string | null | undefined) =>
    routeDiscipline(discipline, sectionId, sections)

  // One block per competitor, in name order, each with their climbs in the
  // order the wall is numbered: by discipline, then by route number.
  const boards = competitionBoards(discipline)
  const climbOrder = (result: CompetitionResult) => {
    const index = routes.findIndex((r) => r.route_id === result.route_id)
    const number = Number.parseInt(routes[index]?.label ?? '', 10)
    return [
      boards.indexOf(wallOf(result.route?.section_id)),
      Number.isFinite(number) ? number : Number.MAX_SAFE_INTEGER,
      index,
    ]
  }
  const byUser = new Map<string, CompetitionResult[]>()
  for (const result of results) {
    byUser.set(result.user_id, [...(byUser.get(result.user_id) ?? []), result])
  }
  const competitors = [...byUser.entries()]
    .map(([userId, climbs]) => {
      const entry = entries.find((e) => e.user_id === userId)
      return {
        userId,
        entry,
        profile: entry?.profile ?? climbs[0].profile,
        climbs: [...climbs].sort((a, b) => {
          const [ka, kb] = [climbOrder(a), climbOrder(b)]
          return ka[0] - kb[0] || ka[1] - kb[1] || ka[2] - kb[2]
        }),
      }
    })
    .sort((a, b) => fullName(a.profile).localeCompare(fullName(b.profile)))

  const categories = [
    ...new Map(
      competitors.flatMap((c) =>
        c.entry?.category ? [[c.entry.category.id, c.entry.category]] : [],
      ),
    ).values(),
  ] as { id: string; name: string }[]
  const inCategory = (id: string) => competitors.filter((c) => c.entry?.category_id === id).length
  // A filter on a category nobody has results in any more shows everyone
  // rather than an empty list with no way to tell why.
  const activeCategory = categories.some((c) => c.id === categoryFilter) ? categoryFilter : ALL
  const q = search.trim().toLowerCase()
  const visible = competitors.filter(
    (c) =>
      (activeCategory === ALL || c.entry?.category_id === activeCategory) &&
      (!q ||
        [fullName(c.profile), c.profile?.nickname, c.profile?.email].some((v) =>
          v?.toLowerCase().includes(q),
        )),
  )
  const openKeys = expanded ?? new Set(visible.length === 1 ? [visible[0].userId] : [])
  const allLabel = t('competitions.filterAll', { count: competitors.length })

  async function updateAttempts(id: string, value: string) {
    const attempts = value ? Number(value) : null
    if (attempts !== null && (!Number.isInteger(attempts) || attempts < 1)) return
    const token = await getAccessTokenSilently()
    await fetch(`${apiUrl}/competition-ascents/${id}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ attempts }),
    })
    onRefresh()
  }

  async function deleteResult(id: string) {
    const token = await getAccessTokenSilently()
    await fetch(`${apiUrl}/competition-ascents/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
    onRefresh()
  }

  async function addResult() {
    if (!addUserId || !addRouteId) {
      setError(t('competitions.errorResultFields'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/competitions/${competitionId}/results`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: addUserId,
          route_id: addRouteId,
          attempts: addAttempts ? Number(addAttempts) : null,
        }),
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { code?: string }
        setError(
          body.code === 'PROFILE_GENDER_REQUIRED'
            ? t('competitions.errorMemberNoGender')
            : t('competitions.errorSaveFailed'),
        )
        return
      }
      setAddUserId(null)
      setAddRouteId(null)
      setAddAttempts('')
      onRefresh()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {results.length === 0 ? (
        <Card className="shadow-sm">
          <CardBody className="py-8 text-center text-foreground/60">
            {t('competitions.resultsEmpty')}
          </CardBody>
        </Card>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <MemberSearchInput
              value={search}
              onValueChange={(v) => {
                setSearch(v)
                setExpanded(null)
              }}
            />
            {categories.length > 1 && (
              <Select
                size="sm"
                label={t('competitions.colCategory')}
                selectedKeys={[activeCategory]}
                onSelectionChange={(keys) => {
                  // Clicking the selected item again hands back an empty set.
                  const val = Array.from(keys)[0]
                  if (!val) return
                  setCategoryFilter(String(val))
                  setExpanded(null)
                }}
              >
                {[
                  <SelectItem key={ALL} textValue={allLabel}>
                    {allLabel}
                  </SelectItem>,
                  ...categories.map((c) => (
                    <SelectItem key={c.id} textValue={`${c.name} (${inCategory(c.id)})`}>
                      {c.name} ({inCategory(c.id)})
                    </SelectItem>
                  )),
                ]}
              </Select>
            )}
          </div>

          {visible.length === 0 ? (
            <Card className="shadow-sm">
              <CardBody className="py-8 text-center text-foreground/60">
                {t('competitions.competitorsNoMatch')}
              </CardBody>
            </Card>
          ) : (
            <Accordion
              variant="splitted"
              selectionMode="multiple"
              className="px-0"
              selectedKeys={openKeys}
              onSelectionChange={(keys) =>
                setExpanded(
                  keys === 'all'
                    ? new Set(visible.map((c) => c.userId))
                    : new Set([...keys].map(String)),
                )
              }
            >
              {visible.map(({ userId, entry, profile, climbs }) => (
                <AccordionItem
                  key={userId}
                  aria-label={fullName(profile)}
                  title={
                    <MemberIdentity
                      userId={userId}
                      name={fullName(profile)}
                      nickname={profile?.nickname}
                      email={profile?.email}
                      avatarExt={profile?.avatar_ext}
                      avatarUpdatedAt={profile?.avatar_updated_at}
                      primaryColor={primaryColor}
                      size="sm"
                    />
                  }
                  subtitle={
                    <span className="mt-1 flex flex-wrap items-center gap-1.5">
                      {entry?.category && (
                        <Chip size="sm" variant="flat">
                          {entry.category.name}
                        </Chip>
                      )}
                      {entry?.session && (
                        <Chip size="sm" variant="flat">
                          {groupLabel(entry.session)}
                        </Chip>
                      )}
                      <Chip size="sm" variant="flat" color="primary">
                        {t('competitions.routeCount', { count: climbs.length })}
                      </Chip>
                    </span>
                  }
                >
                  <div className="flex flex-col gap-2 pb-2">
                    {/* The header above is itself a button, so the profile
                        cannot open from the name the way it does elsewhere. */}
                    <div>
                      <Button
                        size="sm"
                        variant="light"
                        onPress={() =>
                          setProfileTarget({ userId, childId: entry?.child_id ?? null })
                        }
                      >
                        {t('members.viewProfile')}
                      </Button>
                    </div>
                    {/* A four-column table does not fit a phone. It scrolls
                        inside its own box, so the page itself never does. */}
                    <div className="overflow-x-auto">
                      <Table
                        aria-label={`${t('competitions.tabResults')}: ${fullName(profile)}`}
                        removeWrapper
                      >
                        <TableHeader>
                          <TableColumn>{t('competitions.colRoute')}</TableColumn>
                          <TableColumn>{t('competitions.colAttempts')}</TableColumn>
                          <TableColumn>{t('competitions.colSource')}</TableColumn>
                          <TableColumn> </TableColumn>
                        </TableHeader>
                        <TableBody>
                          {climbs.map((result) => {
                            const ref = refFor(result.route_id)
                            const route = ref?.route ?? null
                            const label = labelFor(result.route_id)
                            const grade =
                              result.route?.grade != null
                                ? gradeDisplay(
                                    gradeConfig,
                                    result.route.grade,
                                    wallOf(result.route.section_id),
                                  )
                                : null
                            const badge = grade ? softBadgeColors(grade.color) : null
                            const identity = (
                              <>
                                {route?.image_r2_key && (
                                  <img
                                    src={`${apiUrl}/climbing-routes/${route.id}/image?size=thumb`}
                                    alt=""
                                    loading="lazy"
                                    className="h-10 w-10 flex-none rounded-md object-cover"
                                  />
                                )}
                                <span className="font-medium">{label}</span>
                                {grade && badge && (
                                  <Chip
                                    size="sm"
                                    radius="sm"
                                    className="font-semibold"
                                    style={{ backgroundColor: badge.bg, color: badge.fg }}
                                  >
                                    {grade.label}
                                  </Chip>
                                )}
                                <span className="flex items-center gap-1 text-xs text-foreground/60">
                                  <span
                                    className="h-3 w-3 flex-none rounded-full border border-black/10 shadow-sm"
                                    style={{ backgroundColor: result.route?.color ?? '#d4d4d8' }}
                                  />
                                  {holdColorName(result.route?.color)}
                                </span>
                              </>
                            )
                            return (
                              <TableRow key={result.id}>
                                <TableCell>
                                  {ref?.route ? (
                                    <Button
                                      variant="light"
                                      className="h-auto min-w-0 justify-start gap-2 p-1 text-left"
                                      aria-label={t('competitions.viewRouteDetails', { label })}
                                      onPress={() => setDetailRef(ref)}
                                    >
                                      {identity}
                                    </Button>
                                  ) : (
                                    // Since removed from the competition: the
                                    // result stands, but there is nothing to open.
                                    <span className="flex items-center gap-2 p-1">{identity}</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Input
                                    size="sm"
                                    type="number"
                                    min={1}
                                    className="w-20"
                                    aria-label={t('competitions.colAttempts')}
                                    defaultValue={result.attempts ? String(result.attempts) : ''}
                                    onBlur={(e) =>
                                      void updateAttempts(
                                        result.id,
                                        (e.target as HTMLInputElement).value,
                                      )
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  <span className="text-xs text-foreground/60">
                                    {result.recorded_by
                                      ? t('competitions.sourceStaff')
                                      : t('competitions.sourceSelf')}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  <Button
                                    size="sm"
                                    variant="light"
                                    color="danger"
                                    onPress={() => void deleteResult(result.id)}
                                  >
                                    {t('common.delete')}
                                  </Button>
                                </TableCell>
                              </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </>
      )}

      <Card className="shadow-sm">
        <CardBody className="gap-3">
          <h3 className="font-semibold">{t('competitions.addResult')}</h3>
          <div className="grid gap-3 sm:grid-cols-3">
            <Autocomplete
              label={t('competitions.colMember')}
              selectedKey={addUserId}
              onSelectionChange={(key) => setAddUserId(key ? String(key) : null)}
            >
              {entries.map((entry) => (
                <AutocompleteItem key={entry.user_id} textValue={fullName(entry.profile)}>
                  {fullName(entry.profile)}
                </AutocompleteItem>
              ))}
            </Autocomplete>
            <Select
              label={t('competitions.colRoute')}
              selectedKeys={addRouteId ? [addRouteId] : []}
              onSelectionChange={(keys) => setAddRouteId(([...keys][0] as string) ?? null)}
            >
              {routes.map((ref, index) => {
                const label = nameFor(ref, `${index + 1}`)
                return (
                  <SelectItem key={ref.route_id} textValue={label}>
                    {label}
                  </SelectItem>
                )
              })}
            </Select>
            <Input
              type="number"
              min={1}
              label={t('competitions.colAttempts')}
              value={addAttempts}
              onValueChange={setAddAttempts}
            />
          </div>
          {error && <p className="text-sm text-danger">{error}</p>}
          <div>
            <Button color="primary" size="sm" isLoading={busy} onPress={() => void addResult()}>
              {t('competitions.addResult')}
            </Button>
          </div>
        </CardBody>
      </Card>

      <CompetitionRouteDetailModal
        route={detailRef?.route ?? null}
        title={detailRef ? labelFor(detailRef.route_id) : ''}
        discipline={wallOf(detailRef?.route?.section_id)}
        sectionName={sections.find((s) => s.id === detailRef?.route?.section_id)?.name}
        roundName={rounds.find((r) => r.id === detailRef?.round_id)?.name}
        topCount={detailRef ? results.filter((r) => r.route_id === detailRef.route_id).length : 0}
        apiUrl={apiUrl}
        onClose={() => setDetailRef(null)}
      />

      {profileTarget && (
        <CompetitorProfileModal target={profileTarget} onClose={() => setProfileTarget(null)} />
      )}
    </div>
  )
}
